import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { CommunicationLocaleSchema } from 'types/locale';
import { z } from 'zod/v4';

const URL = '/communication_locales';

const UpdateCommunicationLocalesVariablesSchema = z.object({
  locales: z.array(CommunicationLocaleSchema),
});

export type UpdateCommunicationLocalesVariables = z.infer<
  typeof UpdateCommunicationLocalesVariablesSchema
>;

async function updateCommunicationLocalesFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateCommunicationLocalesVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateCommunicationLocalesVariablesSchema.parse(variables));
}

export function useUpdateCommunicationLocales() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateCommunicationLocalesVariables>({
    mutationFn: variables => updateCommunicationLocalesFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['communicationLocales'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
      client.invalidateQueries({ queryKey: ['template-v2'] });
      client.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateCommunicationLocalesVariables,
    'updateCommunicationLocales'
  >('updateCommunicationLocales', mutation);
}
